import { useDeleteCompanyById } from "@/entities/delete-company-by-id/fetch";
import { useGetCompanyById } from "@/entities/get-company-by-id/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useUpdateCompanyById } from "@/entities/update-company-by-id/fetch";
import type { CompanyEditFormSchemaType } from "@/pages/company-edit/model/schema";
import type { CompanyEditPageProps } from "@/pages/company-edit/model/types";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

type Props = {
  companyId: number;
  isXmileSystemAdmin: boolean;
};

export const useCompanyEditPageProps = (props: Props): CompanyEditPageProps => {
  const { companyId, isXmileSystemAdmin } = props;

  const { data, isLoading, error, refetch } = useGetCompanyById({
    id: companyId,
  });

  const company = useMemo(() => {
    if (error) {
      return {
        status: "error",
        error: {
          message: JSON.stringify(error),
        },
      } as const;
    }

    if (!data || isLoading) {
      return {
        status: "loading",
        data: undefined,
      } as const;
    }

    return {
      status: "success",
      data: data.data,
    } as const;
  }, [data, error, isLoading]);

  const updateCompanyById = useUpdateCompanyById();
  const { setGlobalNotification } = useGlobalNotification();
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (formValues: CompanyEditFormSchemaType) => {
      const result = await updateCompanyById.mutateAsync({
        id: companyId,
        data: {
          ...formValues,
          // 単一選択値を配列に変換してAPIに送信
          category: [formValues.category] as ["shipper" | "carrier"],
        },
      });

      if (isSuccessStatusCode(result.status)) {
        await refetch();
        setGlobalNotification({
          variant: "success",
          message: "会社情報を更新しました",
        });

        if (isXmileSystemAdmin) {
          navigate({
            to: "/admin/companies",
          });
        }
      } else {
        setGlobalNotification({
          variant: "error",
          message: `会社情報の更新に失敗しました\n${JSON.stringify(result.data)}`,
        });
      }
    },
    [
      companyId,
      updateCompanyById,
      setGlobalNotification,
      navigate,
      refetch,
      isXmileSystemAdmin,
    ],
  );

  const [
    isConfirmDeleteCompanyDialogOpen,
    setIsConfirmDeleteCompanyDialogOpen,
  ] = useState(false);
  const deleteCompanyById = useDeleteCompanyById();

  const onDelete = useCallback(async () => {
    try {
      await deleteCompanyById.mutateAsync({
        id: companyId,
      });

      setGlobalNotification({
        variant: "success",
        message: "会社を削除しました",
      });

      navigate({
        to: "/admin/companies",
      });
    } catch (e) {
      setGlobalNotification({
        variant: "error",
        message: `会社の削除に失敗しました\n${JSON.stringify(e)}`,
      });
    } finally {
      setIsConfirmDeleteCompanyDialogOpen(false);
    }
  }, [companyId, deleteCompanyById, navigate, setGlobalNotification]);

  return {
    company,
    onSubmit,
    onDelete,
    isConfirmDeleteCompanyDialogOpen,
    setIsConfirmDeleteCompanyDialogOpen,
    isXmileSystemAdmin,
  };
};
